import React, { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Compass, Shield, Users2, Sparkles } from 'lucide-react';

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('admin@hrcompass.io');
  const [password, setPassword] = useState('Admin@123');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden md:flex flex-col justify-between p-10 bg-gradient-to-br from-primary/10 via-background to-[hsl(28_70%_94%)]">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-display font-semibold">H</div>
            <div>
              <p className="font-display text-xl font-semibold">HR Compass</p>
              <p className="text-xs text-muted-foreground">Consultancy operations, calmly.</p>
            </div>
          </div>
        </div>
        <div>
          <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight">Every client. Every candidate.<br/> One calm workspace.</h2>
          <p className="text-muted-foreground mt-4 max-w-md">Run recruitment, onboarding, HR policies, training, offboarding and payroll for all your client companies from a single, focused command center.</p>
          <div className="grid grid-cols-3 gap-3 mt-8">
            <div className="rounded-lg border bg-card p-3"><Compass className="h-4 w-4 text-primary" /><p className="text-xs mt-1">Follow-up radar</p></div>
            <div className="rounded-lg border bg-card p-3"><Sparkles className="h-4 w-4 text-primary" /><p className="text-xs mt-1">AI drafts</p></div>
            <div className="rounded-lg border bg-card p-3"><Shield className="h-4 w-4 text-primary" /><p className="text-xs mt-1">Role-based access</p></div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} HR Compass</p>
      </div>

      {/* Login card */}
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8">
          <div className="md:hidden flex items-center gap-2 mb-6">
            <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-display font-semibold">H</div>
            <div>
              <p className="font-display text-xl font-semibold">HR Compass</p>
              <p className="text-xs text-muted-foreground">Consultancy operations, calmly.</p>
            </div>
          </div>
          <h1 className="font-display text-2xl font-semibold">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to your HR Compass workspace.</p>

          <form onSubmit={submit} className="space-y-4 mt-6">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-button">
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <div className="mt-6 rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Demo credentials</p>
            <p><span className="font-mono">admin@hrcompass.io</span> · <span className="font-mono">Admin@123</span></p>
            <p className="mt-1">You can create Manager and Staff users from Admin → Users.</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
